import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services';
import { getCurrentUserProfile } from '../services/api/userApi';
import { initKeycloak } from '../services/keycloak';
import useSessionTimeout from '../hooks/useSessionTimeout';
import SessionTimeoutModal from '../components/common/Modal/SessionTimeoutModal';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch full profile from backend to get public_id etc
  const refreshUserProfile = useCallback(async (baseUser) => {
    if (!baseUser) return null;
    try {
      console.log('[AuthContext] Refreshing full user profile...');
      const profileData = await getCurrentUserProfile();
      const updatedUser = { ...baseUser, ...profileData };
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      console.error('[AuthContext] Failed to fetch user profile', error);
      setUser(baseUser);
      return baseUser;
    }
  }, []);

  // Session timeout integration
  // Only monitor timeout if user is authenticated
  const {
    timeLeft,
    showWarning,
    isExpired,
    extendSession
  } = useSessionTimeout({
    warningThreshold: 60, // Show warning 60s before expiration
    onLogout: () => logout()
  });

  useEffect(() => {
    // Initialize Keycloak
    initKeycloak(async () => {
      const currentUser = authService.getCurrentUser();
      if (currentUser) {
        await refreshUserProfile(currentUser);
      } else {
        setUser(null);
      }
      setLoading(false);
    }, (error) => {
      console.error('Keycloak initialization failed', error);
      setLoading(false); // Stop loading to allow fallback UI

      const publicPaths = ['/', '/login', '/error', '/shop'];
      const isPublicPath = publicPaths.includes(window.location.pathname);

      // Only redirect to error page if we are trying to access a protected area
      if (!isPublicPath) {
        window.location.href = '/error';
      }
    });
  }, [refreshUserProfile]);

  const login = async (username, password) => {
    const success = await authService.login(username, password);
    if (success) {
      const currentUser = authService.getCurrentUser();
      await refreshUserProfile(currentUser);
    }
    return success;
  };

  const logout = async () => {
    console.log('[AuthContext] Logout initiated...');
    await authService.logout();
    console.log('[AuthContext] Keycloak logout complete');
    setUser(null);
    console.log('[AuthContext] User state cleared, redirecting to /login');
    // Redirect to login page for security
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, refreshUserProfile }}>
      {!loading && children}

      {/* Session Timeout Modal */}
      {user && (
        <SessionTimeoutModal
          isOpen={showWarning}
          isExpired={isExpired}
          timeLeft={timeLeft}
          onExtend={extendSession}
          onLogout={logout}
        />
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
